import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

interface SEOProps {
    title?: string;
    description?: string;
    keywords?: string;
    image?: string;
    url?: string;
    type?: string;
}

const SEO: React.FC<SEOProps> = ({
    image = 'https://villapinosalhaurin.com/images/pool/main.jpg',
    url = 'https://villapinosalhaurin.com',
    type = 'website',
}) => {
    const { i18n } = useTranslation();
    const lang = i18n.language || 'en';

    // Multi-language meta content
    const metaContent = {
        en: {
            title: 'Villa Pinos Alhaurín - Luxury Villa in Pinos de Alhaurín, Málaga',
            description: 'Luxury 5-bedroom villa with private pool in Pinos de Alhaurín, Málaga. Direct booking from owner. Best rates, personalized service. 30km from Málaga Airport.',
            keywords: 'villa Pinos Alhaurín, luxury villa Málaga, villa rental Málaga, holiday villa Alhaurín, villa with pool Málaga, direct booking villa',
        },
        es: {
            title: 'Villa Pinos Alhaurín - Villa de Lujo en Pinos de Alhaurín, Málaga',
            description: 'Villa de lujo de 5 dormitorios con piscina privada en Pinos de Alhaurín, Málaga. Reserva directa con propietario. Mejores tarifas, servicio personalizado. 30km del Aeropuerto de Málaga.',
            keywords: 'villa Pinos Alhaurín, villa de lujo Málaga, alquiler villa Málaga, villa vacaciones Alhaurín, villa con piscina Málaga, reserva directa villa',
        },
        nl: {
            title: 'Villa Pinos Alhaurín - Luxe Villa in Pinos de Alhaurín, Málaga',
            description: 'Luxe villa met 5 slaapkamers en privézwembad in Pinos de Alhaurín, Málaga. Directe boeking bij eigenaar. Beste tarieven, persoonlijke service. 30km van Málaga luchthaven.',
            keywords: 'villa Pinos Alhaurín, luxe villa Málaga, villa huren Málaga, vakantievilla Alhaurín, villa met zwembad Málaga, direct boeken villa',
        },
        de: {
            title: 'Villa Pinos Alhaurín - Luxusvilla in Pinos de Alhaurín, Málaga',
            description: 'Luxusvilla mit 5 Schlafzimmern und privatem Pool in Pinos de Alhaurín, Málaga. Direkte Buchung beim Eigentümer. Beste Preise, persönlicher Service. 30km vom Flughafen Málaga.',
            keywords: 'villa Pinos Alhaurín, luxusvilla Málaga, villa mieten Málaga, ferienvilla Alhaurín, villa mit pool Málaga, direkt buchen villa',
        },
        fr: {
            title: 'Villa Pinos Alhaurín - Villa de Luxe à Pinos de Alhaurín, Málaga',
            description: 'Villa de luxe de 5 chambres avec piscine privée à Pinos de Alhaurín, Málaga. Réservation directe chez propriétaire. Meilleurs tarifs, service personnalisé. 30km de l\'aéroport de Málaga.',
            keywords: 'villa Pinos Alhaurín, villa de luxe Málaga, location villa Málaga, villa vacances Alhaurín, villa avec piscine Málaga, réservation directe villa',
        },
        th: {
            title: 'Villa Pinos Alhaurín - วิลล่าหรูใน Pinos de Alhaurín, Málaga',
            description: 'วิลล่าหรู 5 ห้องนอนพร้อมสระว่ายน้ำส่วนตัวใน Pinos de Alhaurín, Málaga จองdirectกับเจ้าของ ราคาดีที่สุด บริการเฉพาะบุคคล 30km จากสนามบินมาลากา',
            keywords: 'วิลล่า Pinos Alhaurín, วิลล่าหรูมาลากา, เช่าวิลล่ามาลากา, วิลล่าพักผ่อน Alhaurín, วิลล่ามีสระว่ายน้ำมาลากา, จองdirectวิลล่า',
        },
    };

    const content = metaContent[lang as keyof typeof metaContent] || metaContent.en;

    return (
        <Helmet>
            {/* Primary Meta Tags */}
            <html lang={lang} />
            <title>{content.title}</title>
            <meta name="title" content={content.title} />
            <meta name="description" content={content.description} />
            <meta name="keywords" content={content.keywords} />

            {/* Open Graph / Facebook */}
            <meta property="og:type" content={type} />
            <meta property="og:url" content={url} />
            <meta property="og:title" content={content.title} />
            <meta property="og:description" content={content.description} />
            <meta property="og:image" content={image} />
            <meta property="og:locale" content={lang === 'en' ? 'en_US' : `${lang}_${lang.toUpperCase()}`} />

            {/* Twitter */}
            <meta property="twitter:card" content="summary_large_image" />
            <meta property="twitter:url" content={url} />
            <meta property="twitter:title" content={content.title} />
            <meta property="twitter:description" content={content.description} />
            <meta property="twitter:image" content={image} />

            {/* Canonical URL - Language-specific */}
            <link rel="canonical" href={`${url}?lang=${lang}`} />

            {/* Hreflang Tags - Multi-language SEO */}
            <link rel="alternate" hrefLang="en" href={`${url}?lang=en`} />
            <link rel="alternate" hrefLang="es" href={`${url}?lang=es`} />
            <link rel="alternate" hrefLang="nl" href={`${url}?lang=nl`} />
            <link rel="alternate" hrefLang="de" href={`${url}?lang=de`} />
            <link rel="alternate" hrefLang="fr" href={`${url}?lang=fr`} />
            <link rel="alternate" hrefLang="th" href={`${url}?lang=th`} />
            <link rel="alternate" hrefLang="x-default" href={`${url}?lang=en`} />

            {/* Additional SEO */}
            <meta name="robots" content="index, follow" />
            <meta name="language" content={lang} />
            <meta name="revisit-after" content="7 days" />
            <meta name="author" content="Villa Pinos Alhaurín" />
        </Helmet>
    );
};

export default SEO;
